import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCurrentStore } from '@/lib/api/stores.js';
import {
  checkPermission,
  mergeFeatureFlags,
  type StoreFeatureFlags,
} from '@/lib/permissions.js';
import { useAuthStore } from '@/stores/auth.js';

export function usePermissions() {
  const userRole = useAuthStore((s) => s.user?.role ?? 'read_only');

  const storeQuery = useQuery({
    queryKey: ['store-current'],
    queryFn: getCurrentStore,
  });

  const flags = useMemo(
    () =>
      mergeFeatureFlags(
        (storeQuery.data?.featureFlags as Record<string, unknown> | undefined) ?? {},
      ),
    [storeQuery.data?.featureFlags],
  );

  const can = useCallback(
    (flag: string, value?: unknown) =>
      checkPermission({ flags, userRole, flag, value }).allowed,
    [flags, userRole],
  );

  const cannot = useCallback(
    (flag: string, value?: unknown) => !can(flag, value),
    [can],
  );

  const reason = useCallback(
    (flag: string, value?: unknown) =>
      checkPermission({ flags, userRole, flag, value }).reason,
    [flags, userRole],
  );

  return {
    flags,
    isLoading: storeQuery.isLoading,
    can,
    cannot,
    reason,
  };
}
